// mostly copied from https://github.com/mrdoob/three.js/blob/dev/examples/jsm/loaders/OBJLoader.js

import ExternalLoader, {
  GeometryRawData,
  LoaderDataType,
  Material,
  ObjectData
} from "_src/engine/loaders/external/external_loader";
import output from "_src/engine/output";
import fetch from "_fetch";
import objCommon, {State} from "_src/engine/loaders/external/obj/obj_common";
import {messageType} from "_src/engine/ipc";
import workerManager from "_src/engine/worker_manager";
import {StreamProxy} from "_src/engine/loaders/external/utils";

export default class ObjLoader extends ExternalLoader {
  constructor() {
    super();
    this._dataType = LoaderDataType.STREAM;
  }

  async _parse( reader, progress = null, useWorkers = false ) {
    const scope = this;
    const streamProxy = new StreamProxy(reader, progress);
    streamProxy.start();
    const state = new State();

    const loadPromise = streamProxy.getChunk();
    const initPromise = initParser();

    let [dataChunk, taskId] = (await Promise.all([loadPromise, initPromise]));
    if (!dataChunk) {
      throw new Error( "ExternalLoader.ObjLoader: file is empty");
    }

    for (;;) {
      const parsePromise = parseChunk(dataChunk, state, taskId);
      const loadPromise = streamProxy.getChunk();

      dataChunk = (await Promise.all([loadPromise, parsePromise]))[0];
      if (!dataChunk) break;
    }
    await finishParser(taskId);
    const res = await postProcess( state );
    if (progress) progress.emit(progress.LOADING, 1);
    return res;

    ////
    function initParser() {
      return useWorkers ? initParserWorker() : initParserMain();
    }

    async function initParserWorker() {
      const message = {
        type : messageType.START_OBJ,
      };
      const tid = workerManager.generateUniqueID();
      const parseTaskId = (await workerManager.sendMessage(message, [], tid)).data.parseTaskId;
      return parseTaskId;
    }

    async function initParserMain() {
      return objCommon.startParseTask();
    }

    async function parseChunk(dataChunk, state, parseTaskId) {
      useWorkers ? await parseChunkWorker(dataChunk, state, parseTaskId) : await parseChunkMain(dataChunk, state, parseTaskId);
    }

    async function parseChunkMain(dataChunk, state, parseTaskId) {
      const zeroVerticesSizes = [0,0,0];
      objCommon.decode(dataChunk, state, zeroVerticesSizes, parseTaskId);
    }

    async function parseChunkWorker(dataChunk, state, parseTaskId) {
      const verticesSizes = state.getVerticesSizes();
      const message = {
        type : messageType.PARSE_OBJ,
        dataChunk : dataChunk,
        verticesSizes : verticesSizes,
        parseTaskId : parseTaskId,
      };
      const tid = workerManager.generateUniqueID();
      const transfer = [dataChunk.buffer];

      const workerData = (await workerManager.sendMessage(message, transfer, tid)).data;
      mergeState(state, workerData.state);
    }

    async function finishParser(parseTaskId) {
      useWorkers ? await finishParserWorker(parseTaskId) : await finishParserMain(parseTaskId);
    }

    async function finishParserWorker(parseTaskId) {
      const message = {
        type : messageType.FINISH_OBJ,
        parseTaskId: parseTaskId,
      };
      const tid = workerManager.generateUniqueID();
      await workerManager.sendMessage(message, [], tid);
    }

    async function finishParserMain(parseTaskId) {
      return objCommon.finishParseTask(parseTaskId);
    }

    function mergeState(dst, src) {
      dst.materialLibraries = dst.materialLibraries.concat(src.materialLibraries);
      dst.vertices = mergeBuffer(dst.vertices, src.vertices);
      dst.unnamedObject = mergeBuffer(dst.unnamedObject, src.unnamedObject);
      for (const [name, buffer] of Object.entries(src.objects)) {
        dst.objects[name] = mergeBuffer(dst.objects[name], buffer);
      }

      function mergeBuffer(dst, src) {
        if (!dst) return src;
        if (!src) return dst;

        for (const bufferType of [src.POSITIONS, src.INDICES, src.NORMALS, src.UVS, src.NORMALS_INDICES, src.UVS_INDICES]) {
          const srcBuffer = src.storage[bufferType];
          objCommon.pushToBuffer(dst, bufferType, srcBuffer);
        }
        if (dst.storage[dst.NORMALS_INDICES] && !src.storage[src.NORMALS_INDICES]) dst.push(dst.NORMALS_INDICES, src.INDICES);
        if (dst.storage[dst.UVS_INDICES] && !src.storage[src.UVS_INDICES]) dst.push(dst.UVS_INDICES, src.INDICES);

        return dst;
      }
    }

    async function postProcess( state ) {
      const bufferSizeLimit = 1850000000 / 4; //1.8gb / 4bytes per float ot uint
      const objectsData = [];

      const verticesBuffer = state.vertices;
      objCommon.shrinkBufferToFit(verticesBuffer);

      const indicesBuffers = Object.values(state.objects).concat(state.unnamedObject).filter(buffer => Boolean(buffer) && Boolean(buffer.storage[buffer.INDICES]));
      for (const indicesBuffer of indicesBuffers) {
        objCommon.shrinkBufferToFit(indicesBuffer);

        let positions, indices, normals, uvs;
        const needToCopyVertices = indicesBuffers.length > 1 || indicesBuffer.storage[indicesBuffer.UVS_INDICES] || indicesBuffer.storage[indicesBuffer.NORMALS_INDICES]
        if (needToCopyVertices) {
          if (indicesBuffer.storage[indicesBuffer.INDICES].length * 3 > bufferSizeLimit) {
            throw new Error( "ExternalLoader.ObjLoader: model is too big");
          }
          positions = new Float32Array(indicesBuffer.storage[indicesBuffer.INDICES].length * 3);
          normals = verticesBuffer.storage[verticesBuffer.NORMALS] ? new Float32Array(indicesBuffer.storage[indicesBuffer.INDICES].length * 3) : null;
          uvs = verticesBuffer.storage[verticesBuffer.UVS] ? new Float32Array(indicesBuffer.storage[indicesBuffer.INDICES].length * 2) : null;
          indices = new Uint32Array(indicesBuffer.storage[indicesBuffer.INDICES].length);
          const positionsIndices = indicesBuffer.storage[indicesBuffer.INDICES];
          const uVsIndices = indicesBuffer.storage[indicesBuffer.UVS_INDICES] ? indicesBuffer.storage[indicesBuffer.UVS_INDICES] : positionsIndices;
          const normalsIndices = indicesBuffer.storage[indicesBuffer.NORMALS_INDICES] ? indicesBuffer.storage[indicesBuffer.NORMALS_INDICES] : positionsIndices;
          for (let i=0; i<positionsIndices.length; i++) {
            positions[i*3] = verticesBuffer.storage[verticesBuffer.POSITIONS][positionsIndices[i]*3];
            positions[i*3+1] = verticesBuffer.storage[verticesBuffer.POSITIONS][positionsIndices[i]*3+1];
            positions[i*3+2] = verticesBuffer.storage[verticesBuffer.POSITIONS][positionsIndices[i]*3+2];

            if (normals) {
              normals[i * 3] = verticesBuffer.storage[verticesBuffer.NORMALS][normalsIndices[i] * 3];
              normals[i * 3 + 1] = verticesBuffer.storage[verticesBuffer.NORMALS][normalsIndices[i] * 3 + 1];
              normals[i * 3 + 2] = verticesBuffer.storage[verticesBuffer.NORMALS][normalsIndices[i] * 3 + 2];
            }

            if (uvs) {
              uvs[i * 2] = verticesBuffer.storage[verticesBuffer.UVS][uVsIndices[i] * 2];
              uvs[i * 2 + 1] = verticesBuffer.storage[verticesBuffer.UVS][uVsIndices[i] * 2 + 1];
            }

            indices[i] = i;
          }

        } else {
          positions = verticesBuffer.storage[verticesBuffer.POSITIONS];
          normals = verticesBuffer.storage[verticesBuffer.NORMALS];
          uvs = verticesBuffer.storage[verticesBuffer.UVS];
          indices = indicesBuffer.storage[indicesBuffer.INDICES];
        }

        const geometry = new GeometryRawData(positions);
        geometry.indices = indices;
        geometry.normals = normals;
        geometry.uvs = uvs;
        const material = new Material();
        const objectData = new ObjectData(geometry, material);

        objectsData.push(objectData);
      }

      const textures = new Set();
      const basePath = scope._basePath;
      for (const mtlLibrary of (new Set(state.materialLibraries)).values()) {
        const texturePaths = await getTexturePathsFromMtl(basePath + mtlLibrary);
        texturePaths.forEach(texturePath => textures.add(basePath + texturePath));
      }
      const texturesArray = Array.from(textures);
      for (let i=0; i<objectsData.length; i++) {
        const texture = texturesArray[i];
        if (texture) objectsData[i].material.baseTexture = texture;
      }

      return objectsData;
    }

    async function getTexturePathsFromMtl(path) {
      const response = await fetch(path);
      if (!response.ok) {
        output.warn("Mtl file was not loaded from " + path);
        return [];
      }

      let text;
      try {
        text = await response.text();
      } catch (error) {
        output.warn("Mtl file was not loaded from " + path + ". Err: " + error);
        return [];
      }

      const lines = text.split( "\n" );
      const texturePaths = [];

      for ( let i = 0; i < lines.length; i ++ ) {

        let line = lines[ i ];
        line = line.trim();

        if ( line.length === 0 || line.charAt( 0 ) === "#" ) {

          // Blank line or comment ignore
          continue;

        }

        const pos = line.indexOf( " " );

        let key = ( pos >= 0 ) ? line.substring( 0, pos ) : line;
        key = key.toLowerCase();

        let value = ( pos >= 0 ) ? line.substring( pos + 1 ) : "";
        value = value.trim();

        if ( key === "map_kd") {
          texturePaths.push(value);
        }
      }

      return texturePaths;
    }
  }
}
